/**
 * Nearest-fit table selection.
 *
 * Customer-facing booking flows must not let a small party claim a much larger
 * table (e.g. 2 guests on a table for 8). A table is only offered when it is
 * big enough for the party *and* close to the smallest table that still fits —
 * the "best fit" — plus a small buffer so guests aren't left with nothing when
 * the exact size is already taken.
 *
 * Rule for a party of N, given the available tables:
 *   bestFit = smallest available capacity with capacity >= N
 *   a table is allowed when  N <= capacity <= bestFit + BUFFER
 */
export const NEAREST_FIT_BUFFER = 2;

export interface FitCandidate {
  capacity?: number | null;
  isAvailable: boolean;
}

/**
 * Smallest capacity among the available tables that can still seat the party,
 * or `null` when the party size is invalid or nothing available fits.
 */
export function nearestFitCapacity(
  tables: readonly FitCandidate[],
  partySize: number,
): number | null {
  if (!Number.isFinite(partySize) || partySize < 1) return null;
  let best: number | null = null;
  for (const table of tables) {
    const capacity = Number(table.capacity);
    if (!table.isAvailable || !Number.isFinite(capacity) || capacity < partySize) {
      continue;
    }
    if (best === null || capacity < best) best = capacity;
  }
  return best;
}

/**
 * Whether a table of `capacity` is a nearest-fit pick for `partySize`, given
 * the `bestFitCapacity` for the current set of available tables — i.e. big
 * enough for the party and within `buffer` seats of the best available fit.
 */
export function isNearestFitTable(
  capacity: number | null | undefined,
  partySize: number,
  bestFitCapacity: number | null,
  buffer: number = NEAREST_FIT_BUFFER,
): boolean {
  const cap = Number(capacity);
  if (bestFitCapacity === null) return false;
  if (!Number.isFinite(cap)) return false;
  if (!Number.isFinite(partySize) || partySize < 1) return false;
  return cap >= partySize && cap <= bestFitCapacity + buffer;
}

/**
 * Picks the single best available table for the party: the smallest capacity
 * that fits, breaking ties by table id (numeric-aware) for stable, predictable
 * assignment. Returns `null` when nothing available fits.
 */
export function pickNearestFitTable<T extends FitCandidate & { tableId: string }>(
  tables: readonly T[],
  partySize: number,
): T | null {
  const bestFit = nearestFitCapacity(tables, partySize);
  if (bestFit === null) return null;
  const candidates = tables
    .filter(
      (table) =>
        table.isAvailable &&
        Number.isFinite(Number(table.capacity)) &&
        Number(table.capacity) === bestFit,
    )
    .sort((a, b) =>
      String(a.tableId).localeCompare(String(b.tableId), undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  return candidates[0] ?? null;
}
